import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses } from '../../types/admin';
import type { CaseStatus } from '../../types/admin';
import { formatDate, formatPrice } from '../../utils/format';
type Filter = 'Alle' | CaseStatus;
export function Cases() {
  const {
    cases,
    customerById
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return cases.filter(item => {
      const customer = customerById(item.customerId);
      const matchesFilter = filter === 'Alle' || item.status === filter;
      const haystack = `${item.number} ${item.title} ${item.address} ${customer?.name ?? item.customerName}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [cases, customerById, filter, query]);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-386" data-mp="3gdkq9qltk4ra">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-387" data-mp="3ubslnu37iy0c">
        <div data-id="element-388" data-mp="3pbepb9ayxci5">
          <p className="text-sm text-ink-muted" data-id="element-389" data-mp="3pbmyaq3yzxo2">{cases.length} sager i alt</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-390" data-mp="83j6eqn0cao6">
            Sager
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-391" data-mp="32vk2i9q4i0a">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-392" data-mp="gvbewvsh19q4" />
          Opret sag
        </Link>
      </div>

      <div className="mt-6" data-id="element-393" data-mp="cwm5tfegueca">
        <label htmlFor="sagsoegning" className="sr-only" data-id="element-394" data-mp="qu0zwxfnzaal">
          Søg i sager
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-395" data-mp="lv7hcqlist3k">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-396" data-mp="3fn5s98qvbzoj" />
          <input id="sagsoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på kunde, sagsnummer, opgave eller adresse" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-397" data-mp="3flcmrwrzrli4" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér sager efter status" data-id="element-398" data-mp="277xlaj3q5kf">
          {(['Alle', ...caseStatuses] as Filter[]).map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-399" data-mp="g6zgnpoh6c5a">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-400" data-mp="1vrro5k4kj8va">
        <div className="hidden md:block" data-id="element-401" data-mp="2teqmdzawsova">
          <table className="w-full text-left text-[15px]" data-id="element-402" data-mp="2tdp8zo6f7qae">
            <thead data-id="element-403" data-mp="2ofx9a7zjazvn">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-404" data-mp="2ofe1vl7iftvv">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-405" data-mp="2jft1r7gltfhk">Sag</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-406" data-mp="2fezcoarfdun3">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-407" data-mp="2fh22atoo1lra">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-408" data-mp="182yyu6igbz9n">Adresse</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-409" data-mp="13yr8k4wcib9b">Planlagt</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-410" data-mp="2rw07f79cx111">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-411" data-mp="2wn8fnzyyagcm">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-412" data-mp="2wojkm344tu5b" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-413" data-mp="2i05ixvigcbqc">
              {visible.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-414" data-mp="2mxyl8dgmwllc">
                  <td className="px-5 py-3.5 font-medium text-ink" data-id="element-415" data-mp="3ansxotx1k852">#{item.number}</td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-416" data-mp="3flkr5sdc42bd">
                    {customerById(item.customerId)?.name ?? item.customerName}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-417" data-mp="35ndnucbyxiru">{item.title}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-418" data-mp="35prem8zm88tl">{item.address || '—'}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-419" data-mp="1q996ux47ub44">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-420" data-mp="1mp8pa376w07z"><StatusBadge status={item.status} data-id="element-421" data-mp="1mopfj9rp0i51" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-422" data-mp="1mrbmb76retm7">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-423" data-mp="1wmwn5203dnud">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-424" data-mp="1rnsf4eowlw5m">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-425" data-mp="yxti9x4gwuqe">
          {visible.map(item => <li key={item.id} data-id="element-426" data-mp="1dqnbelcnx9dj">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-427" data-mp="18y3txdautu3g">
                <div className="flex items-start justify-between gap-3" data-id="element-428" data-mp="l89ld7xkirp0">
                  <div className="min-w-0" data-id="element-429" data-mp="l6f1lwy195mi">
                    <p className="text-xs text-ink-faint" data-id="element-430" data-mp="g9ojl42o3zyg">Sag #{item.number}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-431" data-mp="15xahyn2zcvz9">{item.title}</p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-432" data-mp="19yw28thugcjh">
                      {customerById(item.customerId)?.name ?? item.customerName}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-433" data-mp="1jopabkis29iz" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-434" data-mp="1jqtdg08ur4i7">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>

        {visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-435" data-mp="vycs9gttiweo">
            Ingen sager matcher søgningen.
          </p>}
      </div>
    </div>;
}